import type {Role} from '@redstack/shared'
import {apiClient} from './client'

export type {Role}

export type User = {
	id: string
	email: string | null
	firstName: string | null
	lastName: string | null
	role: Role
	deactivatedAt: string | null
	avatarUrl: string | null
	organizationId: string | null
	impersonating?: boolean
	realUserRole?: Role
}

export type PendingInvitation = {
	id: string
	email_address: string
	status: string
	created_at: number
	updated_at: number
	public_metadata?: {
		role?: string
	}
}

export type UnifiedUser = {
	id: string
	email: string
	firstName: string | null
	lastName: string | null
	role: Role | null
	status: 'active' | 'pending' | 'deactivated'
	// For pending users, id is the invitation ID
	// For active users, id is the user ID
}

export async function getUsers(): Promise<{data: User[]}> {
	return apiClient<{data: User[]}>('/api/users')
}

export async function getMe(): Promise<{data: User}> {
	return apiClient<{data: User}>('/api/users/me')
}

export async function getInvitations(): Promise<{data: PendingInvitation[]}> {
	return apiClient<{data: PendingInvitation[]}>('/api/users/invitations')
}

export async function updateUserRole(
	userId: string,
	role: Role,
): Promise<void> {
	await apiClient(`/api/users/${userId}/role`, {
		method: 'PATCH',
		body: {role},
	})
}

export async function deleteUser(userId: string): Promise<void> {
	await apiClient(`/api/users/${userId}`, {
		method: 'DELETE',
	})
}

export async function resendInvitation(invitationId: string): Promise<void> {
	await apiClient(`/api/users/invitations/${invitationId}/resend`, {
		method: 'POST',
	})
}

export async function revokeInvitation(invitationId: string): Promise<void> {
	await apiClient(`/api/users/invitations/${invitationId}`, {
		method: 'DELETE',
	})
}

export async function deactivateUser(userId: string): Promise<void> {
	await apiClient(`/api/users/${userId}/deactivate`, {
		method: 'PATCH',
	})
}

export async function reactivateUser(userId: string): Promise<void> {
	await apiClient(`/api/users/${userId}/reactivate`, {
		method: 'PATCH',
	})
}

export async function inviteUser(
	email: string,
	role: Role = 'USER',
): Promise<void> {
	await apiClient('/api/users/invite', {
		method: 'POST',
		body: {email, role},
	})
}
